import { useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useGetDocumentIndexings } from '../../api/useGetDocumentIndexings';
import { useGetDocumentIndexingStats } from '../../api/useGetDocumentIndexingStats';
import { DOCUMENTS_PAGE_SIZE_OPTIONS } from '../../constants/pagination';
import type {
  DocumentIndexingFilter,
  DocumentIndexingItem,
} from '../../types/documentIndexing';

export type Props = {
  spaceId: string;
};

export function useDocumentIndexingSettingsCard(props: Props) {
  const [statusFilter, setStatusFilter] = useState<DocumentIndexingFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DOCUMENTS_PAGE_SIZE_OPTIONS[0]);

  const [selectedContentItem, setSelectedContentItem] = useState<DocumentIndexingItem | null>(null);
  const [selectedErrorItem, setSelectedErrorItem] = useState<DocumentIndexingItem | null>(null);

  const statsQuery = useGetDocumentIndexingStats(props.spaceId);

  const listParams = useMemo(
    () => ({
      page,
      pageSize,
      search: search.trim() || undefined,
      status: statusFilter,
    }),
    [page, pageSize, search, statusFilter]
  );

  const listQuery = useGetDocumentIndexings(props.spaceId, listParams);

  const stats = statsQuery.data;

  const formattedLastIndexation = useMemo(() => {
    if (!stats?.lastExtractedAt) {
      return null;
    }
    try {
      const date = new Date(stats.lastExtractedAt);
      const formatted = format(date, "d MMMM yyyy 'à' HH:mm", { locale: fr });
      const relative = formatDistanceToNow(date, { addSuffix: true, locale: fr });
      return `${formatted} (${relative})`;
    } catch {
      return null;
    }
  }, [stats?.lastExtractedAt]);

  function handleStatusFilterChange(filter: DocumentIndexingFilter) {
    setStatusFilter(filter);
    setPage(0);
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(0);
  }

  function handleResetFilters() {
    setStatusFilter('all');
    setSearch('');
    setPage(0);
  }

  function handlePageChange(newPage: number) {
    setPage(newPage);
  }

  function handleRowsPerPageChange(newSize: number) {
    setPageSize(newSize);
    setPage(0);
  }

  function handleOpenContentModal(item: DocumentIndexingItem) {
    setSelectedContentItem(item);
  }

  function handleCloseContentModal() {
    setSelectedContentItem(null);
  }

  function handleOpenErrorModal(item: DocumentIndexingItem) {
    setSelectedErrorItem(item);
  }

  function handleCloseErrorModal() {
    setSelectedErrorItem(null);
  }

  function handleRefresh() {
    statsQuery.refetch();
    listQuery.refetch();
  }

  const isInitialLoading = statsQuery.isLoading && !statsQuery.data;
  const isListLoading = listQuery.isLoading;
  const isError = statsQuery.isError || listQuery.isError;
  const isRefreshing = statsQuery.isFetching || listQuery.isFetching;

  const items = listQuery.data?.data ?? [];
  const totalCount = listQuery.data?.count ?? 0;
  const hasNoResult = !isListLoading && items.length === 0 && (search.length > 0 || statusFilter !== 'all');
  const isOverallEmpty = !isListLoading && (stats?.total ?? 0) === 0;

  return {
    stats,
    formattedLastIndexation,
    statusFilter,
    search,
    page,
    pageSize,
    items,
    totalCount,
    isInitialLoading,
    isListLoading,
    isError,
    isRefreshing,
    hasNoResult,
    isOverallEmpty,
    selectedContentItem,
    selectedErrorItem,
    handleStatusFilterChange,
    handleSearchChange,
    handleResetFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenContentModal,
    handleCloseContentModal,
    handleOpenErrorModal,
    handleCloseErrorModal,
    handleRefresh,
  };
}

